// Heatmaps on a top-down plan. The plan is the tour's own model seen from above, cut just above eye
// height on each floor so walls read as solid lines. Four layers:
//   time   where people stood (darker = more time)
//   walk   the routes they walked (darker = more people)
//   look   what they looked at: the surface in the centre of their view
//   flow   moves between renders: arrows sized by how often, dots sized by visits
// The heat layers use one blue scale, light (little) to dark (lots).
import * as THREE from 'three';
import { loadModel } from '@archviewvr/engine';
import { esc, html } from './ui';
import { fmtNum, fmtTime } from './charts';

export interface PlanPano { id: string; label: string; x: number; y: number; z: number; level: number | null; seconds: number; visits: number }
export type Cell = [number, number, number, number];
export interface PlanInput {
  modelUrl: string;
  levels: Array<{ name: string; height: number }>;
  panos: PlanPano[];
  cells: Cell[];
  looks: Cell[];
  paths: Array<[number, number, number, number, number, number]>;
  flow: Array<[string, string, number]>;
}
export type Layer = 'time' | 'walk' | 'look' | 'flow';

const LAYERS: Array<{ id: Layer; label: string; note: string }> = [
  { id: 'time', label: 'Time spent', note: 'Where people stood. Grey is a little, through green and yellow to red for the most time.' },
  { id: 'walk', label: 'Walked', note: 'The routes people walked. Darker means more people.' },
  { id: 'look', label: 'Looked at', note: 'What people faced most: the spot in the middle of their view. Grey is a little, through green and yellow to red for the most looking.' },
  { id: 'flow', label: 'Flow', note: 'How people moved between views. Thicker arrows are more common moves; bigger dots were visited more.' },
];
const CELL = 0.5; // metres per cell (matches tour_places)
const toRgb = (list: string[]) => list.map((h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)));
/** Blue scale for walked routes. */
const BLUE = toRgb(['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b']);
/** Heat scale for time spent and looked at: grey (a little) through green and yellow to red (a lot). */
const HEAT = toRgb(['#b9c0c8', '#7cc576', '#3fae49', '#c9d936', '#f5c518', '#f08a1c', '#d83a2b']);
export const HEAT_CSS = 'linear-gradient(90deg, #b9c0c8, #7cc576, #3fae49, #c9d936, #f5c518, #f08a1c, #d83a2b)';
const BLUE_CSS = 'linear-gradient(90deg, #cde2fb, #6da7ec, #256abf, #0d366b)';
function rampAt(ramp: number[][], t: number): [number, number, number] {
  const f = Math.min(0.9999, Math.max(0, t)) * (ramp.length - 1);
  const i = Math.floor(f); const k = f - i;
  return [0, 1, 2].map((c) => ramp[i][c] + (ramp[i + 1][c] - ramp[i][c]) * k) as [number, number, number];
}
const nearestLevel = (levels: number[], y: number) => levels.reduce((best, h, i) => (Math.abs(h - y) < Math.abs(levels[best] - y) ? i : best), 0);

export async function heatPlan(host: HTMLElement, input: PlanInput, start: Layer = 'time'): Promise<void> {
  host.innerHTML = '<div class="hp-loading muted small">Drawing the plan…</div>';
  let model: THREE.Object3D;
  try { model = await loadModel(input.modelUrl, 'glb'); }
  catch { host.innerHTML = '<p class="muted small">The plan couldn’t be drawn because the model didn’t load.</p>'; return; }

  const heights = input.levels.length ? input.levels.map((l) => l.height) : [1.6];
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const pad = Math.max(size.x, size.z) * 0.04;
  const minX = box.min.x - pad; const maxX = box.max.x + pad; const minZ = box.min.z - pad; const maxZ = box.max.z + pad;
  const aspect = (maxX - minX) / (maxZ - minZ);

  // flat, unlit plan: surfaces seen from above are white; where the cut opens a wall we see its
  // inside faces, drawn dark, so walls read as solid lines like an architect's plan
  const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
  const top = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.FrontSide, clippingPlanes: [plane] });
  const cut = new THREE.MeshBasicMaterial({ color: 0x5b6672, side: THREE.BackSide, clippingPlanes: [plane] });
  const meshes: THREE.Mesh[] = [];
  model.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) meshes.push(m); });
  for (const m of meshes) { m.material = top; m.add(new THREE.Mesh(m.geometry, cut)); }
  const scene = new THREE.Scene();
  scene.add(model);

  const levelTabs = input.levels.length > 1;
  host.innerHTML = html`
    <div class="hp-controls">
      <div class="hp-layers" role="tablist" aria-label="Heatmap">${LAYERS.map((l) => html`<button role="tab" data-layer="${l.id}" aria-selected="${l.id === start ? 'true' : 'false'}">${l.label}</button>`)}</div>
      ${levelTabs ? html`<div class="hp-levels" role="tablist" aria-label="Floor">${input.levels.map((l, i) => html`<button role="tab" data-l="${i}" aria-selected="${i === 0 ? 'true' : 'false'}">${l.name}</button>`)}</div>` : ''}
    </div>
    <p class="hp-note muted small"></p>
    <div class="hp-stage"><canvas class="hp-plan"></canvas><canvas class="hp-heat"></canvas><div class="hp-dots"></div><div class="hp-empty" hidden></div><div class="chart-tip" hidden></div></div>
    <div class="hp-legend"></div>`.html;
  const stage = host.querySelector<HTMLElement>('.hp-stage')!;
  const planCanvas = host.querySelector<HTMLCanvasElement>('.hp-plan')!;
  const heat = host.querySelector<HTMLCanvasElement>('.hp-heat')!;
  const dots = host.querySelector<HTMLElement>('.hp-dots')!;
  const tip = host.querySelector<HTMLElement>('.chart-tip')!;
  const empty = host.querySelector<HTMLElement>('.hp-empty')!;
  const note = host.querySelector<HTMLElement>('.hp-note')!;
  const legend = host.querySelector<HTMLElement>('.hp-legend')!;

  const renderer = new THREE.WebGLRenderer({ canvas: planCanvas, antialias: true, preserveDrawingBuffer: true });
  renderer.localClippingEnabled = true;
  renderer.setClearColor(0xeef2f6, 1);
  const cam = new THREE.OrthographicCamera(minX, maxX, -minZ, -maxZ, 0.1, 1000);
  cam.position.set(0, box.max.y + 50, 0);
  cam.up.set(0, 0, -1);
  cam.lookAt(0, 0, 0);

  const panoLevel = (p: PlanPano) => p.level ?? nearestLevel(heights, p.y);
  const byId = new Map(input.panos.map((p) => [p.id, p]));
  let level = 0;
  let layer: Layer = start;

  const draw = () => {
    const W = Math.max(280, stage.clientWidth);
    const H = Math.min(560, Math.round(W / aspect));
    const Wd = Math.min(W, Math.round(H * aspect));
    const off = (W - Wd) / 2;
    stage.style.height = `${H}px`;
    const dpr = Math.min(2, devicePixelRatio || 1);
    renderer.setPixelRatio(dpr);
    renderer.setSize(Wd, H, true);
    for (const c of [planCanvas, heat]) { c.style.width = `${Wd}px`; c.style.height = `${H}px`; c.style.left = `${off}px`; }
    dots.style.left = `${off}px`; dots.style.width = `${Wd}px`; dots.style.height = `${H}px`;

    plane.constant = heights[level] + 0.3; // cut 0.3 m above this floor's eye height
    cam.left = minX; cam.right = maxX; cam.top = -minZ; cam.bottom = -maxZ;
    cam.updateProjectionMatrix();
    renderer.render(scene, cam);

    const sx = (x: number) => ((x - minX) / (maxX - minX)) * Wd;
    const sz = (z: number) => ((z - minZ) / (maxZ - minZ)) * H;
    const m2px = Wd / (maxX - minX);
    heat.width = Math.round(Wd * dpr); heat.height = Math.round(H * dpr);
    const g = heat.getContext('2d')!;
    g.clearRect(0, 0, heat.width, heat.height);
    const onLevel = (gy: number) => nearestLevel(heights, gy) === level;

    // heat layers: soft marks added up in an alpha buffer, then coloured with a scale
    const colourise = (paint: (a: CanvasRenderingContext2D) => void, ramp = HEAT) => {
      const acc = document.createElement('canvas'); acc.width = heat.width; acc.height = heat.height;
      const a = acc.getContext('2d')!;
      a.globalCompositeOperation = 'lighter';
      paint(a);
      const img = a.getImageData(0, 0, acc.width, acc.height); const d = img.data;
      for (let i = 3; i < d.length; i += 4) {
        const t = d[i] / 255;
        if (t < 0.02) { d[i] = 0; continue; }
        const [r, gg, b] = rampAt(ramp, t);
        d[i - 3] = r; d[i - 2] = gg; d[i - 1] = b; d[i] = Math.round(Math.min(1, 0.35 + t) * 215);
      }
      g.putImageData(img, 0, 0);
    };
    const blobs = (cells: Cell[], radiusM: number) => {
      const list = cells.filter((c) => onLevel(c[2]));
      const max = Math.max(1, ...list.map((c) => c[3]));
      const rad = Math.max(8, radiusM * m2px) * dpr;
      colourise((a) => {
        for (const [gx, gz, , n] of list) {
          const px = sx((gx + 0.5) * CELL) * dpr; const pz = sz((gz + 0.5) * CELL) * dpr;
          const w = Math.min(1, 0.25 + 0.75 * Math.sqrt(n / max)) * 0.55;
          const grd = a.createRadialGradient(px, pz, 0, px, pz, rad);
          grd.addColorStop(0, `rgba(0,0,0,${w})`); grd.addColorStop(1, 'rgba(0,0,0,0)');
          a.fillStyle = grd; a.fillRect(px - rad, pz - rad, rad * 2, rad * 2);
        }
      });
      return list.length;
    };

    let count = 0;
    if (layer === 'time') count = blobs(input.cells, 1.1);
    else if (layer === 'look') count = blobs(input.looks, 0.8);
    else if (layer === 'walk') {
      const list = input.paths.filter((p) => onLevel(p[4]));
      const max = Math.max(1, ...list.map((p) => p[5]));
      colourise((a) => {
        a.lineCap = 'round';
        a.lineWidth = Math.max(3, 0.35 * m2px) * dpr;
        // light strokes that add up: only routes many people took turn dark
        for (const [ax, az, bx, bz, , n] of list) {
          a.strokeStyle = `rgba(0,0,0,${(0.03 + 0.22 * (n / max)).toFixed(3)})`;
          a.beginPath();
          a.moveTo(sx((ax + 0.5) * CELL) * dpr, sz((az + 0.5) * CELL) * dpr);
          a.lineTo(sx((bx + 0.5) * CELL) * dpr, sz((bz + 0.5) * CELL) * dpr);
          a.stroke();
        }
      }, BLUE);
      count = list.length;
    } else {
      // flow: arrows between renders on this floor, each direction drawn slightly to its own side
      const moves = input.flow
        .map(([a, b, n]) => ({ a: byId.get(a), b: byId.get(b), n }))
        .filter((m) => m.a && m.b && panoLevel(m.a) === level && panoLevel(m.b) === level) as Array<{ a: PlanPano; b: PlanPano; n: number }>;
      const max = Math.max(1, ...moves.map((m) => m.n));
      moves.sort((x, y) => x.n - y.n);
      // with lots of moves, leave out the rare ones so the main routes stand out (the table lists them all)
      if (moves.length > 24) moves.splice(0, moves.length, ...moves.filter((m) => m.n >= max * 0.2));
      g.save(); g.scale(dpr, dpr); g.lineCap = 'round';
      for (const m of moves) {
        const x1 = sx(m.a.x); const y1 = sz(m.a.z); const x2 = sx(m.b.x); const y2 = sz(m.b.z);
        const len = Math.hypot(x2 - x1, y2 - y1); if (len < 12) continue;
        const ux = (x2 - x1) / len; const uy = (y2 - y1) / len;
        const w = 1.5 + 6 * Math.sqrt(m.n / max);
        const ox = -uy * (w / 2 + 1.5); const oy = ux * (w / 2 + 1.5); // keep A→B and B→A apart
        const s0 = 9; const s1 = len - 9;
        g.strokeStyle = `rgba(10,112,181,${(0.3 + 0.6 * Math.sqrt(m.n / max)).toFixed(3)})`;
        g.lineWidth = w;
        g.beginPath(); g.moveTo(x1 + ux * s0 + ox, y1 + uy * s0 + oy); g.lineTo(x1 + ux * s1 + ox, y1 + uy * s1 + oy); g.stroke();
        // arrowhead at 60% along
        const hx = x1 + ux * len * 0.6 + ox; const hy = y1 + uy * len * 0.6 + oy; const hs = 4 + w;
        g.fillStyle = g.strokeStyle;
        g.beginPath(); g.moveTo(hx + ux * hs, hy + uy * hs); g.lineTo(hx - uy * hs * 0.6, hy + ux * hs * 0.6); g.lineTo(hx + uy * hs * 0.6, hy - ux * hs * 0.6); g.closePath(); g.fill();
      }
      g.restore();
      count = moves.length;
    }

    // renders on this floor: hover for the room, time and visits. In flow, dot size shows visits.
    dots.innerHTML = '';
    const maxVisits = Math.max(1, ...input.panos.map((p) => p.visits));
    for (const p of input.panos) {
      if (panoLevel(p) !== level) continue;
      const b = document.createElement('button');
      b.className = 'hp-dot'; b.style.left = `${sx(p.x)}px`; b.style.top = `${sz(p.z)}px`;
      if (layer === 'flow') b.style.setProperty('--d', `${(8 + 14 * Math.sqrt(p.visits / maxVisits)).toFixed(1)}px`);
      b.setAttribute('aria-label', `${p.label}: ${fmtTime(p.seconds)}, ${p.visits} visits`);
      const show = () => {
        tip.innerHTML = `<strong>${esc(p.label)}</strong><span>${esc(fmtTime(p.seconds))} spent here · ${esc(fmtNum(p.visits))} visit${p.visits === 1 ? '' : 's'}</span>`;
        tip.hidden = false;
        tip.style.left = `${Math.min(Math.max(0, off + sx(p.x) - tip.offsetWidth / 2), W - tip.offsetWidth)}px`;
        tip.style.top = `${Math.max(0, sz(p.z) - tip.offsetHeight - 16)}px`;
      };
      b.addEventListener('pointerenter', show); b.addEventListener('focus', show);
      b.addEventListener('pointerleave', () => (tip.hidden = true)); b.addEventListener('blur', () => (tip.hidden = true));
      dots.append(b);
    }
    stage.classList.toggle('layer-flow', layer === 'flow');

    const info = LAYERS.find((l) => l.id === layer)!;
    note.textContent = info.note;
    empty.hidden = count > 0;
    const anything = input.cells.length > 0;
    empty.textContent = !anything
      ? 'No walking recorded yet. Open the tour’s share link in a private window or on your phone and look around: views opened from your dashboard aren’t counted.'
      : layer === 'look' && !input.looks.length
        ? 'Looking data is collected from new visits, so this fills in as people view the tour.'
        : 'Nothing on this floor for this period yet. Try the other floor.';
    legend.innerHTML = layer === 'flow'
      ? '<span>Fewer moves</span><svg width="120" height="12" aria-hidden="true"><line x1="4" y1="6" x2="40" y2="6" stroke="#0a70b5" stroke-opacity=".4" stroke-width="2" stroke-linecap="round"/><line x1="56" y1="6" x2="116" y2="6" stroke="#0a70b5" stroke-opacity=".9" stroke-width="7" stroke-linecap="round"/></svg><span>More moves</span>'
      : `<span>Less</span><i style="background:${layer === 'walk' ? BLUE_CSS : HEAT_CSS}"></i><span>More</span>`;
  };

  host.querySelectorAll<HTMLButtonElement>('[data-l]').forEach((b) => (b.onclick = () => {
    level = +b.dataset.l!;
    host.querySelectorAll('[data-l]').forEach((x) => x.setAttribute('aria-selected', String(x === b)));
    draw();
  }));
  host.querySelectorAll<HTMLButtonElement>('[data-layer]').forEach((b) => (b.onclick = () => {
    layer = b.dataset.layer as Layer;
    host.querySelectorAll('[data-layer]').forEach((x) => x.setAttribute('aria-selected', String(x === b)));
    draw();
  }));
  // open on the busiest floor
  if (levelTabs) {
    const per = heights.map((_, i) => input.cells.filter((c) => nearestLevel(heights, c[2]) === i).reduce((s, c) => s + c[3], 0));
    level = per.indexOf(Math.max(...per));
    host.querySelectorAll('[data-l]').forEach((x, i) => x.setAttribute('aria-selected', String(i === level)));
  }
  draw();
  let last = stage.clientWidth;
  new ResizeObserver(() => { if (Math.abs(stage.clientWidth - last) > 4) { last = stage.clientWidth; draw(); } }).observe(stage);
}
